<template>
  <div
    v-if="container"
    ref="wrap"
    :class="boxClass"
    :data-label="label || offscreenLabel"
    :role="boxRole"
    :aria-labelledby="isGroup && labelId ? labelId : null"
    :aria-label="
      isGroup && !labelId && (label || offscreenLabel)
        ? label || offscreenLabel
        : null
    "
  >
    <div v-if="displayBoxHeader" class="cat-box-header">
      <component
        :is="headingComponent"
        :id="labelId"
        tabindex="-1"
        :class="headingComponentClass"
        :data-label-type="dataLabelType"
      >
        {{ label }}
        <cat-icon
          v-if="icon"
          :decorative="!icon.label"
          :class="['cat-box-header-icon', icon.css]"
          :icon="icon.icon"
          :icon-label="icon.label"
        />
      </component>
      <cat-help-trigger
        v-if="help && help.url"
        :sourceId="labelId"
        :url="help.url"
        :title="label"
      />
      <cat-hint v-if="!readonly || forceHint" :id="id + '_hint'" :text="hint" />
      <cat-help
        v-if="help && help.text && (!readonly || forceHelp)"
        :id="id"
        :heading="help.title"
        :text="help.text"
        :aria-describedby="label && labelId ? labelId : null"
      />
      <cat-errors :id="id" :errors="errors" :container="true" />
      <slot name="intro"></slot>
    </div>
    <slot></slot>
  </div>
  <div
    v-else
    ref="wrap"
    :class="boxClass"
    :data-label="offscreenLabel || !label || !forId ? offscreenLabel : null"
    role="group"
    :aria-label="!labelId ? offscreenLabel || label : null"
    :aria-labelledby="labelId"
    :aria-describedby="hint ? id + '_hint' : null"
  >
    <label v-if="label && forId" :id="labelId" :for="forId">{{ label }}</label>
    <cat-hint v-if="!readonly || forceHint" :id="id + '_hint'" :text="hint" />
    <cat-help
      v-if="help && help.text && (!readonly || forceHelp)"
      :id="id"
      :heading="help.title"
      :text="help.text"
      :aria-describedby="label && forId ? labelId : null"
    />
    <slot name="intro"></slot>
    <slot></slot>
    <cat-errors :errors="errors" />
  </div>
</template>

<script lang="ts">
import {
  Component,
  Vue,
  Prop,
  mixins,
  Inject,
  Provide
} from "vue-facing-decorator";
import CatHelp from "../utilities/help.vue";
import Help from "../utilities/help";
import Helpful from "../utilities/helpful";
import PageElementMixin from "../field-element";
import { hasSlotContent } from "@/components/utilities/slot-content.js";
import heightUtilsMixin from "../utilities/heightUtilsMixin";
import CatErrors from "../catErrors/catErrors.vue";
import CatHint from "../utilities/hint.vue";
import CatHelpTrigger from "@/components/catPageTemplate/catHelpTrigger.vue";
import CatIcon from "@/components/utilities/catIcon.vue";
import { PropOptions, nextTick } from "vue";

@Component({
  name: "cat-box",
  components: { CatErrors, CatHelp, CatHelpTrigger, CatHint, CatIcon },
  mixins: [PageElementMixin, Helpful, heightUtilsMixin]
})
export default class CatBox extends Vue {
  @Prop(Boolean) readonly section?: boolean;
  @Prop(Array) readonly errors?: Array<{ level: number; msg: string }>;
  @Prop([String, Number]) readonly forId?: string | number;
  @Prop([String, Number]) readonly labelledby?: string | number;
  @Prop(Object) readonly help?: Help;
  @Prop({ type: Boolean, default: false }) readonly readonly?: boolean;
  @Prop({ type: Boolean, default: false }) readonly forceHelp?: boolean;
  @Prop({ type: Boolean, default: false }) readonly forceHint?: boolean;
  @Prop({ type: Boolean, default: false }) readonly group?: boolean;
  @Prop({ type: Boolean, default: false }) readonly align?: boolean;
  @Prop({ type: String }) readonly hint?: string;
  @Prop({ type: String }) readonly label?: string;
  @Prop({ type: String }) readonly offscreenLabel?: string;
  @Prop({ type: Number }) readonly titleLevel?: number;
  @Prop({ type: String }) readonly role?: string;
  @Prop({
    type: Array,
    description: "List of classes to be set in the box heading component."
  } as PropOptions<any>)
  public readonly headingClasses?: Array<string>;

  @Prop({
    type: Object,
    description:
      "Object with the following properties - icon, label, css. Eg. { icon: 'check', label: 'Success', css: 'text-success mrt' }"
  } as PropOptions<any>)
  readonly icon?: Object;

  // constructor() {
  //   super();
  // }

  /**
   * Supported roles:
   * - group
   * - region
   *
   */
  get boxRole(): string | null {
    if (this.role == "group" || this.group) {
      return "group";
    }
    if (this.role == "region") {
      return "region";
    }

    return null;
  }

  /**
   * Returns true if the role is a group or region
   */
  get isGroup(): boolean {
    return this.group || this.boxRole != null;
  }

  get displayBoxHeader(): boolean {
    return (
      !!this.label ||
      !!this.hint ||
      !!(this.errors && this.errors.length > 0) ||
      hasSlotContent(this.$slots.intro) ||
      !!this.help
    );
  }

  get boxClass(): object {
    return {
      "cat-box": true,
      fieldset: typeof this.forId !== "undefined",
      invalid:
        this.errors &&
        this.errors.length > 0 &&
        this.errors.some(function (r) {
          return r.level <= 1;
        })
    };
  }

  updated() {
    var t = this;
    nextTick(function () {
      if (t.align) {
        t.sizeSpacers(
          t.$refs.wrap,
          ".fieldset",
          ".cat-error-wrapper",
          ".spacer"
        );
      }
    });
  }

  mounted() {
    var _this = this;
    nextTick(function () {
      if (_this.align) {
        _this.sizeSpacers(
          _this.$refs.wrap,
          ".fieldset",
          ".cat-error-wrapper",
          ".spacer"
        );
      }
    });
  }

  get labelId(): String | number | null {
    if (this.labelledby) {
      return this.labelledby;
    }
    if (!this.label || !this.id) {
      return null;
    }
    return this.id + "_lbl";
  }

  get container(): Boolean {
    return typeof this.forId === "undefined";
  }

  get headingComponent(): String {
    return this.titleLevel
      ? this.titleLevel > 6
        ? "h6"
        : "h" + this.titleLevel
      : this.section
        ? "h2"
        : "h3";
  }

  get headingComponentClass(): Array<string> {
    let css: string[] = [];
    if (this.headingClasses) {
      for (let value of this.headingClasses) {
        css.push(value);
      }
    }
    if (this.help && !!this.help.url) {
      css.push("inline");
    }
    if (this.label && !this.section) {
      css.push("label");
    }
    return css;
  }

  get dataLabelType(): String | null {
    if (this.label && !this.section) {
      return "group-title";
    } else if (this.label && this.section) {
      return "section-title";
    }
    return null;
  }
}
</script>
